import { Building } from '../types/building';
import { RetrofitOption, RetrofitPackage } from '../types/retrofit';
import { RetrofitService } from './retrofitService';
import { LL97Service } from './ll97Service';

export interface FinancingOption {
    type: 'cash' | 'loan' | 'lease' | 'ppa';
    name: string;
    downPayment: number;
    monthlyPayment: number;
    termYears: number;
    interestRate: number;
    totalFinancingCost: number;
}

export interface EconomicChoice {
    id: string;
    name: string;
    description: string;
    totalCost: number;
    annualSavings: number;
    paybackPeriod: number;
    roi10Year: number;
    netPresentValue: number;
    ll97SavingsAnnual: number;
    energySavingsAnnual: number;
    totalSavings10Year: number;
    riskLevel: 'Low' | 'Medium' | 'High';
    complexity: 'Simple' | 'Moderate' | 'Complex';
    retrofitOptions: RetrofitOption[];
    recommendation: string;
    businessCase: string;
    keyBenefits: string[];
    riskMitigation: string[];
    implementationSteps: string[];
    financingOptions: FinancingOption[];
    bestFinancingOption: FinancingOption;
    monthlyNetCashFlow: number;
    cashFlow: {
        year: number;
        investment: number;
        savings: number;
        loanPayment: number;
        netCashFlow: number;
        cumulative: number;
    }[];
}

export class EconomicOptimizerService {
    
    /**
     * Get the top 3 most economical retrofit choices for a building
     */
    static getTop3EconomicChoices(building: Building): EconomicChoice[] {
        const allChoices = this.generateAllEconomicChoices(building);
        
        // Sort by economic efficiency (combination of ROI, payback period, and NPV)
        const sortedChoices = allChoices.sort((a, b) => {
            const scoreA = this.calculateEconomicScore(a);
            const scoreB = this.calculateEconomicScore(b);
            return scoreB - scoreA; // Higher score is better
        });
        
        return sortedChoices.slice(0, 3);
    }
    
    /**
     * Get all available analysis choices for a building
     */
    static getAllAnalysisChoices(building: Building): EconomicChoice[] {
        return this.generateAllEconomicChoices(building);
    }
    
    /**
     * Generate all possible economic choices for a building
     */
    private static generateAllEconomicChoices(building: Building): EconomicChoice[] {
        const choices: EconomicChoice[] = [];
        
        // Choice 1: Minimal Investment (Quick wins)
        const minimalChoice = this.createMinimalInvestmentChoice(building);
        if (minimalChoice) choices.push(minimalChoice);
        
        // Choice 2: Balanced Approach (Medium investment, good ROI)
        const balancedChoice = this.createBalancedChoice(building);
        if (balancedChoice) choices.push(balancedChoice);
        
        // Choice 3: Comprehensive Retrofit (Maximum savings)
        const comprehensiveChoice = this.createComprehensiveChoice(building);
        if (comprehensiveChoice) choices.push(comprehensiveChoice);
        
        // Choice 4: LL97 Compliance Only (Minimum to avoid fines)
        const complianceChoice = this.createComplianceOnlyChoice(building);
        if (complianceChoice) choices.push(complianceChoice);
        
        // Choice 5: Energy Efficiency Focus (Maximum energy savings)
        const efficiencyChoice = this.createEfficiencyFocusChoice(building);
        if (efficiencyChoice) choices.push(efficiencyChoice);
        
        return choices.filter(choice => choice.paybackPeriod <= 20); // Only viable options
    }
    
    /**
     * Create minimal investment choice (LED, smart thermostats, basic weatherization)
     */
    private static createMinimalInvestmentChoice(building: Building): EconomicChoice | null {
        const lowCostOptions = RetrofitService.getApplicableRetrofitOptions(building)
            .filter(option => option.averageCostPerSqFt <= 8)
            .sort((a, b) => b.averageEnergyReduction - a.averageEnergyReduction)
            .slice(0, 3);
        
        if (lowCostOptions.length === 0) return null;
        
        const totalCost = this.calculateTotalRetrofitCost(building, lowCostOptions);
        const annualSavings = this.calculateAnnualSavings(building, lowCostOptions);
        const ll97Savings = this.calculateLL97Savings(building, lowCostOptions);
        const energySavings = annualSavings - ll97Savings;
        const paybackPeriod = totalCost / annualSavings;
        
        // Generate financing options and select the best one
        const financingOptions = this.generateFinancingOptions(totalCost);
        const bestFinancingOption = this.selectBestFinancingOption(financingOptions, annualSavings);
        
        return {
            id: 'minimal',
            name: 'Quick Wins Package',
            description: 'Low-cost, high-impact improvements with fast payback',
            totalCost,
            annualSavings,
            paybackPeriod,
            roi10Year: (annualSavings * 10 / totalCost) * 100,
            netPresentValue: this.calculateNPV(totalCost, annualSavings, 10, 0.05),
            ll97SavingsAnnual: ll97Savings,
            energySavingsAnnual: energySavings,
            totalSavings10Year: annualSavings * 10,
            riskLevel: 'Low',
            complexity: 'Simple',
            retrofitOptions: lowCostOptions,
            recommendation: 'Perfect for immediate cost savings with minimal disruption',
            businessCase: this.generateBusinessCase('minimal', totalCost, annualSavings, ll97Savings, energySavings, paybackPeriod, bestFinancingOption),
            keyBenefits: this.generateKeyBenefits('minimal', ll97Savings, energySavings, lowCostOptions),
            riskMitigation: this.generateRiskMitigation('minimal', 'Low'),
            implementationSteps: this.generateImplementationSteps('minimal', 'Simple'),
            financingOptions,
            bestFinancingOption,
            monthlyNetCashFlow: (annualSavings / 12) - bestFinancingOption.monthlyPayment,
            cashFlow: this.generateEnhancedCashFlow(totalCost, annualSavings, 10, bestFinancingOption)
        };
    }
    
    /**
     * Create balanced investment choice (mix of quick wins and major upgrades)
     */
    private static createBalancedChoice(building: Building): EconomicChoice | null {
        const allOptions = RetrofitService.getApplicableRetrofitOptions(building);
        const balancedOptions = [
            ...allOptions.filter(opt => opt.averageCostPerSqFt <= 8).slice(0, 2), // 2 quick wins
            ...allOptions.filter(opt => opt.averageCostPerSqFt > 8 && opt.averageCostPerSqFt <= 25).slice(0, 2) // 2 medium investments
        ];
        
        if (balancedOptions.length === 0) return null;
        
        const totalCost = this.calculateTotalRetrofitCost(building, balancedOptions);
        const annualSavings = this.calculateAnnualSavings(building, balancedOptions);
        const ll97Savings = this.calculateLL97Savings(building, balancedOptions);
        const energySavings = annualSavings - ll97Savings;
        const paybackPeriod = totalCost / annualSavings;
        
        // Generate financing options and select the best one
        const financingOptions = this.generateFinancingOptions(totalCost);
        const bestFinancingOption = this.selectBestFinancingOption(financingOptions, annualSavings);
        
        return {
            id: 'balanced',
            name: 'Balanced Investment Plan',
            description: 'Optimal mix of quick wins and major improvements',
            totalCost,
            annualSavings,
            paybackPeriod,
            roi10Year: (annualSavings * 10 / totalCost) * 100,
            netPresentValue: this.calculateNPV(totalCost, annualSavings, 10, 0.05),
            ll97SavingsAnnual: ll97Savings,
            energySavingsAnnual: energySavings,
            totalSavings10Year: annualSavings * 10,
            riskLevel: 'Medium',
            complexity: 'Moderate',
            retrofitOptions: balancedOptions,
            recommendation: 'Best overall value combining immediate and long-term benefits',
            businessCase: this.generateBusinessCase('balanced', totalCost, annualSavings, ll97Savings, energySavings, paybackPeriod, bestFinancingOption),
            keyBenefits: this.generateKeyBenefits('balanced', ll97Savings, energySavings, balancedOptions),
            riskMitigation: this.generateRiskMitigation('balanced', 'Medium'),
            implementationSteps: this.generateImplementationSteps('balanced', 'Moderate'),
            financingOptions,
            bestFinancingOption,
            monthlyNetCashFlow: (annualSavings / 12) - bestFinancingOption.monthlyPayment,
            cashFlow: this.generateEnhancedCashFlow(totalCost, annualSavings, 10, bestFinancingOption)
        };
    }
    
    /**
     * Create comprehensive retrofit choice (maximum energy savings)
     */
    private static createComprehensiveChoice(building: Building): EconomicChoice | null {
        const allOptions = RetrofitService.getApplicableRetrofitOptions(building);
        const comprehensiveOptions = allOptions.slice(0, 5); // Top 5 most effective options
        
        if (comprehensiveOptions.length === 0) return null;
        
        const totalCost = this.calculateTotalRetrofitCost(building, comprehensiveOptions);
        const annualSavings = this.calculateAnnualSavings(building, comprehensiveOptions);
        const ll97Savings = this.calculateLL97Savings(building, comprehensiveOptions);
        const energySavings = annualSavings - ll97Savings;
        const paybackPeriod = totalCost / annualSavings;
        
        // Generate financing options and select the best one
        const financingOptions = this.generateFinancingOptions(totalCost);
        const bestFinancingOption = this.selectBestFinancingOption(financingOptions, annualSavings);
        
        return {
            id: 'comprehensive',
            name: 'Complete Building Transformation',
            description: 'Maximum energy savings and future-proofing investment',
            totalCost,
            annualSavings,
            paybackPeriod,
            roi10Year: (annualSavings * 10 / totalCost) * 100,
            netPresentValue: this.calculateNPV(totalCost, annualSavings, 15, 0.05),
            ll97SavingsAnnual: ll97Savings,
            energySavingsAnnual: energySavings,
            totalSavings10Year: annualSavings * 15, // 15-year view for comprehensive
            riskLevel: 'Medium',
            complexity: 'Complex',
            retrofitOptions: comprehensiveOptions,
            recommendation: 'Maximum long-term value and complete LL97 compliance',
            businessCase: this.generateBusinessCase('comprehensive', totalCost, annualSavings, ll97Savings, energySavings, paybackPeriod, bestFinancingOption),
            keyBenefits: this.generateKeyBenefits('comprehensive', ll97Savings, energySavings, comprehensiveOptions),
            riskMitigation: this.generateRiskMitigation('comprehensive', 'Medium'),
            implementationSteps: this.generateImplementationSteps('comprehensive', 'Complex'),
            financingOptions,
            bestFinancingOption,
            monthlyNetCashFlow: (annualSavings / 12) - bestFinancingOption.monthlyPayment,
            cashFlow: this.generateEnhancedCashFlow(totalCost, annualSavings, 15, bestFinancingOption)
        };
    }
    
    /**
     * Create LL97 compliance-only choice
     */
    private static createComplianceOnlyChoice(building: Building): EconomicChoice | null {
        const ll97Gap = LL97Service.calculateEmissionsGap(
            building.buildingType,
            building.grossFloorArea,
            building.totalGHGEmissions,
            2025
        );
        
        if (ll97Gap.gap <= 0) return null; // Already compliant
        
        // Find minimal retrofits needed for compliance
        const complianceOptions = RetrofitService.getApplicableRetrofitOptions(building)
            .filter(option => option.averageGHGReduction > 0)
            .sort((a, b) => a.averageCostPerSqFt - b.averageCostPerSqFt) // Cheapest first
            .slice(0, 2); // Minimal options for compliance
        
        if (complianceOptions.length === 0) return null;
        
        const totalCost = this.calculateTotalRetrofitCost(building, complianceOptions);
        const annualSavings = this.calculateAnnualSavings(building, complianceOptions);
        const ll97Savings = ll97Gap.gap * 268; // Avoid the full fine (gap * $268 per ton)
        const energySavings = annualSavings - ll97Savings;
        const paybackPeriod = totalCost / ll97Savings;
        
        // Generate financing options and select the best one
        const financingOptions = this.generateFinancingOptions(totalCost);
        const bestFinancingOption = this.selectBestFinancingOption(financingOptions, ll97Savings);
        
        return {
            id: 'compliance',
            name: 'LL97 Compliance Package',
            description: 'Minimum investment to avoid LL97 penalties',
            totalCost,
            annualSavings: ll97Savings,
            paybackPeriod,
            roi10Year: (ll97Savings * 10 / totalCost) * 100,
            netPresentValue: this.calculateNPV(totalCost, ll97Savings, 10, 0.05),
            ll97SavingsAnnual: ll97Savings,
            energySavingsAnnual: energySavings,
            totalSavings10Year: ll97Savings * 10,
            riskLevel: 'Low',
            complexity: 'Simple',
            retrofitOptions: complianceOptions,
            recommendation: 'Essential to avoid penalties - minimum viable investment',
            businessCase: this.generateBusinessCase('compliance', totalCost, ll97Savings, ll97Savings, energySavings, paybackPeriod, bestFinancingOption),
            keyBenefits: this.generateKeyBenefits('compliance', ll97Savings, energySavings, complianceOptions),
            riskMitigation: this.generateRiskMitigation('compliance', 'Low'),
            implementationSteps: this.generateImplementationSteps('compliance', 'Simple'),
            financingOptions,
            bestFinancingOption,
            monthlyNetCashFlow: (ll97Savings / 12) - bestFinancingOption.monthlyPayment,
            cashFlow: this.generateEnhancedCashFlow(totalCost, ll97Savings, 10, bestFinancingOption)
        };
    }
    
    /**
     * Create energy efficiency focus choice
     */
    private static createEfficiencyFocusChoice(building: Building): EconomicChoice | null {
        const efficiencyOptions = RetrofitService.getApplicableRetrofitOptions(building)
            .filter(option => option.averageEnergyReduction >= 15)
            .sort((a, b) => b.averageEnergyReduction - a.averageEnergyReduction)
            .slice(0, 3);
        
        if (efficiencyOptions.length === 0) return null;
        
        const totalCost = this.calculateTotalRetrofitCost(building, efficiencyOptions);
        const annualSavings = this.calculateAnnualSavings(building, efficiencyOptions);
        const ll97Savings = this.calculateLL97Savings(building, efficiencyOptions);
        const energySavings = annualSavings - ll97Savings;
        const paybackPeriod = totalCost / annualSavings;
        
        // Generate financing options and select the best one
        const financingOptions = this.generateFinancingOptions(totalCost);
        const bestFinancingOption = this.selectBestFinancingOption(financingOptions, annualSavings);
        
        return {
            id: 'efficiency',
            name: 'Energy Efficiency Focus',
            description: 'Maximum energy savings and operational cost reduction',
            totalCost,
            annualSavings,
            paybackPeriod,
            roi10Year: (annualSavings * 10 / totalCost) * 100,
            netPresentValue: this.calculateNPV(totalCost, annualSavings, 12, 0.05),
            ll97SavingsAnnual: ll97Savings,
            energySavingsAnnual: energySavings,
            totalSavings10Year: annualSavings * 12,
            riskLevel: 'Medium',
            complexity: 'Moderate',
            retrofitOptions: efficiencyOptions,
            recommendation: 'Best for reducing ongoing operational costs',
            businessCase: this.generateBusinessCase('efficiency', totalCost, annualSavings, ll97Savings, energySavings, paybackPeriod, bestFinancingOption),
            keyBenefits: this.generateKeyBenefits('efficiency', ll97Savings, energySavings, efficiencyOptions),
            riskMitigation: this.generateRiskMitigation('efficiency', 'Medium'),
            implementationSteps: this.generateImplementationSteps('efficiency', 'Moderate'),
            financingOptions,
            bestFinancingOption,
            monthlyNetCashFlow: (annualSavings / 12) - bestFinancingOption.monthlyPayment,
            cashFlow: this.generateEnhancedCashFlow(totalCost, annualSavings, 12, bestFinancingOption)
        };
    }
    
    /**
     * Calculate economic score for ranking choices
     */
    private static calculateEconomicScore(choice: EconomicChoice): number {
        // Weighted scoring: ROI (40%) + Payback Period (30%) + NPV (20%) + Risk (10%)
        const roiScore = Math.min(choice.roi10Year / 200, 1) * 40; // Cap at 200% ROI
        const paybackScore = Math.max(0, (20 - choice.paybackPeriod) / 20) * 30; // Better score for shorter payback
        const npvScore = Math.min(choice.netPresentValue / choice.totalCost, 2) * 20; // NPV relative to investment
        const riskScore = (choice.riskLevel === 'Low' ? 10 : choice.riskLevel === 'Medium' ? 6 : 2);
        
        return roiScore + paybackScore + npvScore + riskScore;
    }
    
    /**
     * Calculate total retrofit cost for selected options
     */
    private static calculateTotalRetrofitCost(building: Building, options: RetrofitOption[]): number {
        return options.reduce((total, option) => 
            total + (option.averageCostPerSqFt * building.grossFloorArea), 0
        );
    }
    
    /**
     * Calculate annual savings from selected retrofits using compound reduction
     */
    private static calculateAnnualSavings(building: Building, options: RetrofitOption[]): number {
        // Use compound reduction instead of linear addition
        const energySavingsPercent = this.calculateCombinedReduction(
            options.map(option => option.averageEnergyReduction)
        );
        
        const currentEnergyBill = this.getBuildingSpecificEnergyCost(building);
        return currentEnergyBill * (energySavingsPercent / 100);
    }
    
    /**
     * Calculate LL97 savings from avoided fines using compound reduction
     */
    private static calculateLL97Savings(building: Building, options: RetrofitOption[]): number {
        // Use compound reduction instead of linear addition
        const ghgReductionPercent = this.calculateCombinedReduction(
            options.map(option => option.averageGHGReduction)
        );
        
        const currentFine = LL97Service.calculateLL97Fine(
            building.buildingType,
            building.grossFloorArea,
            building.totalGHGEmissions,
            2025
        );
        
        return currentFine.currentAnnualFine * (ghgReductionPercent / 100);
    }

    /**
     * Calculate compound reduction for multiple retrofits (diminishing returns)
     */
    private static calculateCombinedReduction(reductions: number[]): number {
        // Account for diminishing returns when combining retrofits
        return reductions.reduce((combined, reduction) => {
            return combined + reduction * (1 - combined / 100);
        }, 0);
    }

    /**
     * Get building-specific energy cost instead of flat $2.50/sqft
     */
    private static getBuildingSpecificEnergyCost(building: Building): number {
        const baseCostPerSqFt = this.getBaseCostByBuildingType(building.buildingType);
        
        // Adjust for building age (older buildings tend to have higher energy costs)
        const currentYear = new Date().getFullYear();
        const buildingAge = building.yearBuilt ? currentYear - building.yearBuilt : 30; // Default 30 years if unknown
        const ageFactor = Math.min(1.5, 1 + (buildingAge / 100)); // Max 50% increase for very old buildings
        
        // Adjust for building size (economies of scale for larger buildings)
        const sizeFactor = building.grossFloorArea > 100000 ? 0.9 : 
                          building.grossFloorArea > 50000 ? 0.95 : 1.0;
        
        return building.grossFloorArea * baseCostPerSqFt * ageFactor * sizeFactor;
    }

    /**
     * Get base energy cost per square foot by building type
     */
    private static getBaseCostByBuildingType(buildingType: string): number {
        const energyCostMap: { [key: string]: number } = {
            'Office': 3.2,              // High HVAC and lighting demands
            'Retail': 2.8,              // Moderate energy use with customer comfort needs
            'Mixed Use': 2.9,           // Combination of office and residential
            'Multifamily Housing': 2.1, // Lower per-sqft costs due to residential efficiency
            'Hotel': 3.8,               // Highest due to 24/7 operations, hot water, comfort
            'Industrial': 2.5,          // Variable but generally lower per sqft
            'Warehouse': 1.8,           // Lowest due to minimal HVAC needs
            'School': 2.6,              // Moderate with seasonal variations
            'Hospital': 4.5,            // Highest due to critical systems and 24/7 operations
            'Other': 2.5                // Default fallback
        };
        
        return energyCostMap[buildingType] || energyCostMap['Other'];
    }
    
    /**
     * Calculate Net Present Value
     */
    private static calculateNPV(initialCost: number, annualSavings: number, years: number, discountRate: number): number {
        let npv = -initialCost;
        
        for (let year = 1; year <= years; year++) {
            npv += annualSavings / Math.pow(1 + discountRate, year);
        }
        
        return npv;
    }
    
    /**
     * Generate cash flow projections
     */
    private static generateCashFlow(initialCost: number, annualSavings: number, years: number) {
        const cashFlow = [];
        let cumulative = -initialCost;
        
        // Year 0 (investment)
        cashFlow.push({
            year: 0,
            investment: initialCost,
            savings: 0,
            cumulative
        });
        
        // Following years
        for (let year = 1; year <= years; year++) {
            cumulative += annualSavings;
            cashFlow.push({
                year,
                investment: 0,
                savings: annualSavings,
                cumulative
            });
        }
        
        return cashFlow;
    }
    
    /**
     * Generate financing options for a retrofit investment
     */
    private static generateFinancingOptions(totalCost: number): FinancingOption[] {
        const options: FinancingOption[] = [];
        
        // Cash payment option
        options.push({
            type: 'cash',
            name: 'Cash Payment',
            downPayment: totalCost,
            monthlyPayment: 0,
            termYears: 0,
            interestRate: 0,
            totalFinancingCost: totalCost
        });
        
        // Traditional bank loan (5-7 year term)
        const bankLoanRate = 0.065; // 6.5% annual rate
        const bankTermYears = 7;
        const bankMonthlyPayment = this.calculateLoanPayment(totalCost, bankLoanRate, bankTermYears);
        options.push({
            type: 'loan',
            name: 'Bank Loan (7 Year)',
            downPayment: 0,
            monthlyPayment: bankMonthlyPayment,
            termYears: bankTermYears,
            interestRate: bankLoanRate,
            totalFinancingCost: bankMonthlyPayment * 12 * bankTermYears
        });
        
        // SBA loan for energy efficiency (longer term, lower rate)
        const sbaLoanRate = 0.055; // 5.5% annual rate
        const sbaTermYears = 10;
        const sbaMonthlyPayment = this.calculateLoanPayment(totalCost, sbaLoanRate, sbaTermYears);
        options.push({
            type: 'loan',
            name: 'SBA Energy Efficiency Loan',
            downPayment: 0,
            monthlyPayment: sbaMonthlyPayment,
            termYears: sbaTermYears,
            interestRate: sbaLoanRate,
            totalFinancingCost: sbaMonthlyPayment * 12 * sbaTermYears
        });
        
        // Equipment lease option
        const leaseRate = 0.08; // 8% annual rate
        const leaseTermYears = 5;
        const leaseMonthlyPayment = this.calculateLoanPayment(totalCost * 0.85, leaseRate, leaseTermYears); // 85% financing
        options.push({
            type: 'lease',
            name: 'Equipment Lease',
            downPayment: totalCost * 0.15, // 15% down
            monthlyPayment: leaseMonthlyPayment,
            termYears: leaseTermYears,
            interestRate: leaseRate,
            totalFinancingCost: (totalCost * 0.15) + (leaseMonthlyPayment * 12 * leaseTermYears)
        });
        
        return options;
    }
    
    /**
     * Calculate monthly loan payment using standard amortization formula
     */
    private static calculateLoanPayment(principal: number, annualRate: number, years: number): number {
        if (annualRate === 0) return principal / (years * 12);
        
        const monthlyRate = annualRate / 12;
        const numPayments = years * 12;
        
        return principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
               (Math.pow(1 + monthlyRate, numPayments) - 1);
    }
    
    /**
     * Select the best financing option based on total cost of ownership
     */
    private static selectBestFinancingOption(options: FinancingOption[], annualSavings: number): FinancingOption {
        return options.reduce((best, current) => {
            const currentTotalCost = this.calculateTotalCostOfOwnership(current, annualSavings);
            const bestTotalCost = this.calculateTotalCostOfOwnership(best, annualSavings);
            return currentTotalCost < bestTotalCost ? current : best;
        });
    }
    
    /**
     * Calculate consistent total cost of ownership across all financing options
     */
    private static calculateTotalCostOfOwnership(option: FinancingOption, annualSavings: number): number {
        const discountRate = 0.05; // 5% discount rate
        
        if (option.type === 'cash') {
            // For cash: immediate outlay minus present value of savings
            const presentValueOfSavings = this.calculatePresentValueOfSavings(annualSavings, 10, discountRate);
            return option.totalFinancingCost - presentValueOfSavings;
        }
        
        // For financed options: present value of all payments minus present value of savings
        let presentValueOfPayments = option.downPayment;
        
        // Add present value of monthly payments
        for (let month = 1; month <= option.termYears * 12; month++) {
            presentValueOfPayments += option.monthlyPayment / Math.pow(1 + (discountRate / 12), month);
        }
        
        // Subtract present value of energy savings
        const presentValueOfSavings = this.calculatePresentValueOfSavings(annualSavings, option.termYears, discountRate);
        
        return presentValueOfPayments - presentValueOfSavings;
    }

    /**
     * Calculate present value of annual savings stream
     */
    private static calculatePresentValueOfSavings(annualSavings: number, years: number, discountRate: number): number {
        let presentValue = 0;
        for (let year = 1; year <= years; year++) {
            presentValue += annualSavings / Math.pow(1 + discountRate, year);
        }
        return presentValue;
    }
    
    /**
     * Generate business case explanation for building managers
     */
    private static generateBusinessCase(
        choiceId: string, 
        totalCost: number, 
        annualSavings: number, 
        ll97Savings: number,
        energySavings: number,
        paybackPeriod: number,
        bestFinancing: FinancingOption
    ): string {
        switch (choiceId) {
            case 'minimal':
                return `This Quick Wins package delivers immediate results with minimal disruption to operations. 
                With just ${paybackPeriod.toFixed(1)} years to pay back the investment, you'll start seeing positive 
                cash flow quickly. The ${this.formatCurrencyShort(bestFinancing.monthlyPayment)} monthly payment 
                is easily covered by the ${this.formatCurrencyShort(annualSavings / 12)} monthly energy savings, 
                providing immediate positive cash flow from day one.`;
                
            case 'balanced':
                return `This balanced approach combines quick wins with strategic upgrades for optimal ROI. 
                The ${this.formatCurrencyShort(bestFinancing.monthlyPayment)} monthly investment generates 
                ${this.formatCurrencyShort(annualSavings / 12)} in monthly savings, creating positive cash flow 
                while positioning your building for future regulations. With ${paybackPeriod.toFixed(1)} year payback, 
                this plan balances immediate returns with long-term value.`;
                
            case 'comprehensive':
                return `This comprehensive transformation maximizes your building's potential and future-proofs 
                against tightening regulations. While requiring a larger investment of 
                ${this.formatCurrencyShort(bestFinancing.monthlyPayment)} monthly, the substantial 
                ${this.formatCurrencyShort(annualSavings / 12)} monthly savings create strong positive cash flow. 
                The ${paybackPeriod.toFixed(1)} year payback delivers maximum long-term value and tenant appeal.`;
                
            case 'compliance':
                return `This essential compliance package protects your building from costly LL97 penalties of 
                ${this.formatCurrencyShort(ll97Savings)} annually. The modest 
                ${this.formatCurrencyShort(bestFinancing.monthlyPayment)} monthly investment immediately eliminates 
                penalty risk while generating positive returns. This is the minimum viable investment to avoid 
                regulatory exposure.`;
                
            case 'efficiency':
                return `This energy-focused strategy maximizes operational cost reduction through proven efficiency 
                measures. The ${this.formatCurrencyShort(bestFinancing.monthlyPayment)} monthly investment yields 
                ${this.formatCurrencyShort(energySavings / 12)} in monthly energy cost savings alone, with additional 
                LL97 benefits. The ${paybackPeriod.toFixed(1)} year payback makes this an excellent operational 
                efficiency play.`;
                
            default:
                return `This retrofit investment provides strong financial returns with manageable monthly payments 
                of ${this.formatCurrencyShort(bestFinancing.monthlyPayment)} covered by 
                ${this.formatCurrencyShort(annualSavings / 12)} monthly savings.`;
        }
    }
    
    /**
     * Generate key benefits list for building managers
     */
    private static generateKeyBenefits(
        choiceId: string,
        ll97Savings: number,
        energySavings: number,
        options: RetrofitOption[]
    ): string[] {
        const benefits: string[] = [];
        
        // Common benefits
        if (ll97Savings > 0) {
            benefits.push(`Avoid ${this.formatCurrencyShort(ll97Savings)} in annual LL97 penalties`);
        }
        if (energySavings > 0) {
            benefits.push(`Reduce energy costs by ${this.formatCurrencyShort(energySavings)} annually`);
        }
        
        // Choice-specific benefits
        switch (choiceId) {
            case 'minimal':
                benefits.push('Minimal disruption to tenants and operations');
                benefits.push('Quick implementation (2-4 weeks)');
                benefits.push('Immediate positive cash flow');
                break;
                
            case 'balanced':
                benefits.push('Optimal balance of cost and savings');
                benefits.push('Moderate implementation timeline (2-4 months)');
                benefits.push('Enhanced tenant comfort and satisfaction');
                break;
                
            case 'comprehensive':
                benefits.push('Maximum energy savings and efficiency gains');
                benefits.push('Significant increase in property value');
                benefits.push('Premium tenant appeal and retention');
                benefits.push('Future-proof against stricter regulations');
                break;
                
            case 'compliance':
                benefits.push('Eliminates regulatory compliance risk');
                benefits.push('Minimal upfront investment required');
                benefits.push('Protection from escalating penalty costs');
                break;
                
            case 'efficiency':
                benefits.push('Maximum operational cost reduction');
                benefits.push('Improved building performance metrics');
                benefits.push('Enhanced Energy Star score potential');
                break;
        }
        
        // Technology-specific benefits
        if (options.some(opt => opt.name.toLowerCase().includes('hvac'))) {
            benefits.push('Improved tenant comfort and air quality');
        }
        if (options.some(opt => opt.name.toLowerCase().includes('led'))) {
            benefits.push('Better lighting quality and reduced maintenance');
        }
        if (options.some(opt => opt.name.toLowerCase().includes('insulation'))) {
            benefits.push('Enhanced thermal comfort year-round');
        }
        
        return benefits;
    }
    
    /**
     * Generate risk mitigation strategies
     */
    private static generateRiskMitigation(choiceId: string, riskLevel: string): string[] {
        const mitigation: string[] = [];
        
        if (riskLevel === 'Low') {
            mitigation.push('Proven technologies with established track records');
            mitigation.push('Short payback period minimizes investment exposure');
            mitigation.push('Immediate savings offset monthly payments');
        } else if (riskLevel === 'Medium') {
            mitigation.push('Diversified retrofit portfolio spreads risk');
            mitigation.push('Professional installation with warranties included');
            mitigation.push('Performance monitoring ensures projected savings');
        } else {
            mitigation.push('Comprehensive energy audit validates savings projections');
            mitigation.push('Staged implementation allows for adjustments');
            mitigation.push('Performance guarantees from qualified contractors');
        }
        
        // Choice-specific mitigation
        switch (choiceId) {
            case 'compliance':
                mitigation.push('Regulatory compliance eliminates penalty risk');
                break;
            case 'comprehensive':
                mitigation.push('Maximum savings provide buffer against market changes');
                mitigation.push('Premium building position protects against obsolescence');
                break;
        }
        
        return mitigation;
    }
    
    /**
     * Generate implementation steps
     */
    private static generateImplementationSteps(choiceId: string, complexity: string): string[] {
        const steps: string[] = [];
        
        steps.push('1. Secure financing and finalize contractor selection');
        steps.push('2. Obtain necessary permits and approvals');
        
        if (complexity === 'Simple') {
            steps.push('3. Schedule work during off-hours to minimize disruption');
            steps.push('4. Complete installation (2-4 weeks)');
            steps.push('5. Commissioning and system testing');
            steps.push('6. Begin monitoring energy savings');
        } else if (complexity === 'Moderate') {
            steps.push('3. Coordinate with tenants and plan temporary relocations');
            steps.push('4. Phase 1: Non-disruptive improvements (LED, controls)');
            steps.push('5. Phase 2: HVAC and mechanical upgrades');
            steps.push('6. Commissioning and system integration');
            steps.push('7. Performance monitoring and optimization');
        } else {
            steps.push('3. Develop detailed project schedule with tenant coordination');
            steps.push('4. Phase 1: Building envelope improvements');
            steps.push('5. Phase 2: HVAC system upgrades');
            steps.push('6. Phase 3: Controls and monitoring systems');
            steps.push('7. Comprehensive commissioning and testing');
            steps.push('8. 12-month performance monitoring and optimization');
        }
        
        return steps;
    }
    
    /**
     * Generate enhanced cash flow with loan payments
     */
    private static generateEnhancedCashFlow(
        totalCost: number, 
        annualSavings: number, 
        years: number,
        financing: FinancingOption
    ) {
        const cashFlow = [];
        let cumulative = -financing.downPayment;
        
        // Year 0 (down payment)
        cashFlow.push({
            year: 0,
            investment: financing.downPayment,
            savings: 0,
            loanPayment: 0,
            netCashFlow: -financing.downPayment,
            cumulative
        });
        
        const annualLoanPayment = financing.monthlyPayment * 12;
        
        // Following years
        for (let year = 1; year <= years; year++) {
            const loanPayment = year <= financing.termYears ? annualLoanPayment : 0;
            const netCashFlow = annualSavings - loanPayment;
            cumulative += netCashFlow;
            
            cashFlow.push({
                year,
                investment: 0,
                savings: annualSavings,
                loanPayment,
                netCashFlow,
                cumulative
            });
        }
        
        return cashFlow;
    }

    /**
     * Format currency with shortened notation
     */
    static formatCurrencyShort(amount: number): string {
        if (amount >= 1000000) {
            return `$${(amount / 1000000).toFixed(1)}M`;
        } else if (amount >= 1000) {
            return `$${(amount / 1000).toFixed(0)}K`;
        }
        return `$${Math.round(amount).toLocaleString()}`;
    }
    
    /**
     * Get economic summary for a building
     */
    static getEconomicSummary(building: Building) {
        const top3Choices = this.getTop3EconomicChoices(building);
        const bestChoice = top3Choices[0];
        
        return {
            recommendedChoice: bestChoice,
            allChoices: top3Choices,
            summary: {
                bestROI: bestChoice?.roi10Year || 0,
                fastestPayback: Math.min(...top3Choices.map(c => c.paybackPeriod)),
                maxSavings: Math.max(...top3Choices.map(c => c.totalSavings10Year)),
                minInvestment: Math.min(...top3Choices.map(c => c.totalCost))
            }
        };
    }
}
